import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { chatService } from '@/lib/api/services/chat.service';
import { useSubscribeToConversation, useSubscribeToNotifications } from '@/lib/websocket/realtime-chat';

/**
 * Hook to get unread count for all conversations
 */
export function useUnreadCount() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  // Get all conversations
  const { data: conversations = [] } = useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      return await chatService.getConversations();
    },
    enabled: !!user?.id,
    refetchInterval: false, // Use WebSocket for real-time updates
  });

  // Calculate total unread count
  const totalUnread = conversations.reduce((total, conv) => {
    const participant = conv.participants.find((p) => p.user_id === user?.id);
    return total + (participant?.unread_count || 0);
  }, 0);

  // Subscribe to conversation updates to refresh unread counts
  useEffect(() => {
    // This will be handled by the conversation subscription in use-conversations
    // When a conversation is updated via WebSocket, the conversations query will be invalidated
  }, []);

  // Subscribe to notifications to update unread counts
  useSubscribeToNotifications(() => {
    // Invalidate conversations query to refresh unread counts
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  });

  return {
    totalUnread,
    conversations,
  };
}

/**
 * Hook to get unread count for a specific conversation
 */
export function useConversationUnreadCount(conversationId: string | null) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { data: conversation } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      if (!conversationId) return null;
      return await chatService.getConversation(conversationId);
    },
    enabled: !!conversationId && !!user?.id,
    refetchInterval: false,
  });

  // Subscribe to conversation updates
  useSubscribeToConversation(
    conversationId,
    () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
    () => {}
  );

  const participant = conversation?.participants.find((p) => p.user_id === user?.id);
  const unreadCount = participant?.unread_count || 0;

  return {
    unreadCount,
    conversation,
  };
}
